/**
 * KYC Import Parser
 *
 * Reads the FrankieOne Country Coverage xlsx file client-side
 * and produces payloads for the 3 import API endpoints.
 */
import * as XLSX from 'xlsx'
import {
  SUMMARY_CONFIG, KYC_SUMMARY_CONFIG, KYC_CONFIG, RECOMMENDATIONS_CONFIG,
  normalizeBoolean, parseNumber, normalizeHeader, shouldSkipRow,
} from './kycImportConfig'
import type { ColumnMapping, SheetConfig } from './kycImportConfig'
import { COUNTRY_CODES } from './countryCodes'

// ─── Types ─────────────────────────────────────────────────────────────

export interface SheetValidation {
  sheetName: string
  found: boolean
  headerRow: number
  headersFound: string[]
  headersMissing: string[]
  rowCount: number
}

export interface KycImportResult {
  countries: Record<string, unknown>[]
  sources: Record<string, unknown>[]
  recommendations: Record<string, unknown>[]
  validation: SheetValidation[]
  sourceFileName: string
}

// ─── Helpers ───────────────────────────────────────────────────────────

function resolveCountryCode(name: string): string | null {
  return COUNTRY_CODES[name] || COUNTRY_CODES[name.trim()] || null
}

function getStringVal(val: unknown): string | null {
  if (val == null) return null
  const s = String(val).trim()
  return s && s !== 'nan' && s !== 'undefined' ? s : null
}

function buildHeaderMap(row: unknown[]): Map<string, number> {
  const map = new Map<string, number>()
  for (let i = 0; i < row.length; i++) {
    if (row[i] != null) {
      map.set(normalizeHeader(String(row[i])), i)
    }
  }
  return map
}

function findColumnIndex(headerMap: Map<string, number>, mapping: ColumnMapping): number {
  // Exact normalized match
  const norm = normalizeHeader(mapping.header)
  if (headerMap.has(norm)) return headerMap.get(norm)!

  // Partial match — header contains the mapping text
  for (const [h, idx] of headerMap) {
    if (h.includes(norm) || norm.includes(h)) return idx
  }

  // Try just the first few words
  const shortNorm = norm.split(' ').slice(0, 3).join(' ')
  for (const [h, idx] of headerMap) {
    if (h.startsWith(shortNorm)) return idx
  }

  return -1
}

function extractValue(row: unknown[], colIdx: number, mapping: ColumnMapping): unknown {
  if (colIdx < 0 || colIdx >= row.length) return null
  const raw = row[colIdx]

  switch (mapping.type) {
    case 'skip': return null
    case 'boolean': return normalizeBoolean(raw, mapping.boolMode!)
    case 'number': return parseNumber(raw)
    case 'string': return getStringVal(raw)
  }
}

function validateSheet(
  wb: XLSX.WorkBook,
  config: SheetConfig
): SheetValidation {
  const ws = wb.Sheets[config.sheetName]
  if (!ws) {
    return { sheetName: config.sheetName, found: false, headerRow: -1, headersFound: [], headersMissing: config.columns.map(c => c.header), rowCount: 0 }
  }

  if (config.positional) {
    const allRows = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, defval: null })
    const { countryCol, dataStart } = detectSummaryLayout(allRows)
    const dataRows = allRows.slice(dataStart).filter(r => {
      const name = getStringVal(r[countryCol])
      return name && !shouldSkipRow(name)
    })
    return { sheetName: config.sheetName, found: true, headerRow: -1, headersFound: ['(positional)'], headersMissing: [], rowCount: dataRows.length }
  }

  const allRows = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, defval: null })
  const headerRow = allRows[config.headerRowOffset] as unknown[]
  if (!headerRow) {
    return { sheetName: config.sheetName, found: true, headerRow: config.headerRowOffset, headersFound: [], headersMissing: config.columns.map(c => c.header), rowCount: 0 }
  }

  const headerMap = buildHeaderMap(headerRow)
  const found: string[] = []
  const missing: string[] = []

  for (const col of config.columns) {
    if (col.type === 'skip') continue
    const idx = findColumnIndex(headerMap, col)
    if (idx >= 0) found.push(col.header)
    else if (!col.optional) missing.push(col.header)
  }

  const dataRows = allRows.slice(config.headerRowOffset + 1).filter(r => {
    const nameCol = config.columns.find(c => c.field === 'country_name' || c.field === 'country')
    if (!nameCol) return true
    const idx = findColumnIndex(headerMap, nameCol)
    const name = getStringVal(r[idx])
    return name && !shouldSkipRow(name)
  })

  return { sheetName: config.sheetName, found: true, headerRow: config.headerRowOffset, headersFound: found, headersMissing: missing, rowCount: dataRows.length }
}

// ─── Sheet Parsers ─────────────────────────────────────────────────────

/** Find the "Country" header row and column in the SUMMARY sheet to auto-detect layout. */
function detectSummaryLayout(allRows: unknown[][]): { countryCol: number; dataStart: number } {
  for (let r = 0; r < Math.min(20, allRows.length); r++) {
    const row = allRows[r]
    if (!row) continue
    for (let c = 0; c < row.length; c++) {
      if (getStringVal(row[c])?.toLowerCase() === 'country') {
        // Data starts 2 rows after the header (header row + sub-header row)
        return { countryCol: c, dataStart: r + 2 }
      }
    }
  }
  // Fallback to config defaults
  return {
    countryCol: SUMMARY_CONFIG.positionalColumns![0].index,
    dataStart: SUMMARY_CONFIG.positionalDataStart!,
  }
}

function parseSummary(wb: XLSX.WorkBook): Map<string, Record<string, unknown>> {
  const ws = wb.Sheets[SUMMARY_CONFIG.sheetName]
  if (!ws) return new Map()

  const allRows = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, defval: null })
  const { countryCol, dataStart } = detectSummaryLayout(allRows)

  // Calculate offset from configured indices (country_name config index vs actual)
  const configCountryCol = SUMMARY_CONFIG.positionalColumns![0].index
  const offset = countryCol - configCountryCol

  const result = new Map<string, Record<string, unknown>>()

  for (let i = dataStart; i < allRows.length; i++) {
    const row = allRows[i]
    const name = getStringVal(row[countryCol])
    if (!name || shouldSkipRow(name)) continue

    const entry: Record<string, unknown> = {
      country_name: name,
      country_code: resolveCountryCode(name),
    }

    for (const col of SUMMARY_CONFIG.positionalColumns!) {
      if (col.field === 'country_name') continue
      entry[col.field] = col.type === 'boolean'
        ? normalizeBoolean(row[col.index + offset], col.boolMode!)
        : getStringVal(row[col.index + offset])
    }

    result.set(name, entry)
  }

  return result
}

function mergeKycSummary(wb: XLSX.WorkBook, summary: Map<string, Record<string, unknown>>): void {
  const ws = wb.Sheets[KYC_SUMMARY_CONFIG.sheetName]
  if (!ws) return

  const allRows = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, defval: null })
  const headerRow = allRows[KYC_SUMMARY_CONFIG.headerRowOffset] as unknown[]
  if (!headerRow) return

  const headerMap = buildHeaderMap(headerRow)
  const colIndices = new Map<string, number>()
  for (const col of KYC_SUMMARY_CONFIG.columns) {
    colIndices.set(col.field, findColumnIndex(headerMap, col))
  }

  for (let i = KYC_SUMMARY_CONFIG.headerRowOffset + 1; i < allRows.length; i++) {
    const row = allRows[i]
    const nameIdx = colIndices.get('country_name')!
    const name = getStringVal(row[nameIdx])
    if (!name || shouldSkipRow(name)) continue

    const metrics: Record<string, unknown> = {}
    for (const col of KYC_SUMMARY_CONFIG.columns) {
      if (col.field === 'country_name') continue
      const idx = colIndices.get(col.field)!
      metrics[col.field] = extractValue(row, idx, col)
    }

    if (summary.has(name)) {
      Object.assign(summary.get(name)!, metrics)
    } else {
      const entry: Record<string, unknown> = {
        country_name: name,
        country_code: resolveCountryCode(name),
        idv: false, kyc_one_plus: false, kyc_two_plus: false, kyc_gov_id: false,
        kyb_profile: false, kyb_registry_docs: false, kyb_ownership: false, aml: false,
        ...metrics,
      }
      summary.set(name, entry)
    }
  }
}

function parseKycSources(wb: XLSX.WorkBook): Record<string, unknown>[] {
  const ws = wb.Sheets[KYC_CONFIG.sheetName]
  if (!ws) return []

  const allRows = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, defval: null })
  const headerRow = allRows[KYC_CONFIG.headerRowOffset] as unknown[]
  if (!headerRow) return []

  const headerMap = buildHeaderMap(headerRow)
  const colIndices = new Map<string, number>()
  for (const col of KYC_CONFIG.columns) {
    colIndices.set(col.field, findColumnIndex(headerMap, col))
  }

  const results: Record<string, unknown>[] = []

  for (let i = KYC_CONFIG.headerRowOffset + 1; i < allRows.length; i++) {
    const row = allRows[i]
    const countryIdx = colIndices.get('country')!
    const country = getStringVal(row[countryIdx])
    if (!country || shouldSkipRow(country)) continue

    const entry: Record<string, unknown> = { country_code: resolveCountryCode(country) }
    for (const col of KYC_CONFIG.columns) {
      if (col.type === 'skip') continue
      const idx = colIndices.get(col.field)!
      entry[col.field] = extractValue(row, idx, col)
    }

    results.push(entry)
  }

  return results
}

function parseRecommendations(wb: XLSX.WorkBook): Record<string, unknown>[] {
  const ws = wb.Sheets[RECOMMENDATIONS_CONFIG.sheetName]
  if (!ws) return []

  const allRows = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, defval: null })
  const headerRow = allRows[RECOMMENDATIONS_CONFIG.headerRowOffset] as unknown[]
  if (!headerRow) return []

  const headerMap = buildHeaderMap(headerRow)
  const colIndices = new Map<string, number>()
  for (const col of RECOMMENDATIONS_CONFIG.columns) {
    colIndices.set(col.field, findColumnIndex(headerMap, col))
  }

  const results: Record<string, unknown>[] = []

  for (let i = RECOMMENDATIONS_CONFIG.headerRowOffset + 1; i < allRows.length; i++) {
    const row = allRows[i]
    const countryIdx = colIndices.get('country')!
    const country = getStringVal(row[countryIdx])
    if (!country || shouldSkipRow(country)) continue

    const entry: Record<string, unknown> = { country_code: resolveCountryCode(country) }
    for (const col of RECOMMENDATIONS_CONFIG.columns) {
      if (col.type === 'skip') continue
      const idx = colIndices.get(col.field)!
      entry[col.field] = extractValue(row, idx, col)
    }

    results.push(entry)
  }

  return results
}

// ─── Main Entry Point ──────────────────────────────────────────────────

export async function parseKycWorkbook(file: File): Promise<KycImportResult> {
  const buffer = await file.arrayBuffer()
  const data = new Uint8Array(buffer)
  const wb = XLSX.read(data, { type: 'array' })

  // Validate all sheets
  const validation = [
    validateSheet(wb, SUMMARY_CONFIG),
    validateSheet(wb, KYC_SUMMARY_CONFIG),
    validateSheet(wb, KYC_CONFIG),
    validateSheet(wb, RECOMMENDATIONS_CONFIG),
  ]

  // Parse SUMMARY + merge KYC Summary
  const summaryMap = parseSummary(wb)
  mergeKycSummary(wb, summaryMap)
  const countries = Array.from(summaryMap.values())

  // Parse KYC sources
  const sources = parseKycSources(wb)

  // Parse recommendations
  const recommendations = parseRecommendations(wb)

  return {
    countries,
    sources,
    recommendations,
    validation,
    sourceFileName: file.name,
  }
}
